import { useEffect, useRef } from 'react';
import { Icon } from './ui/Icon';
import { Kbd } from './ui/Kbd';
import { useEditor } from '@/store/editor';

type Item = { icon: string; text: string; shortcut?: string; ai?: boolean; onClick?: () => void };

export function CmdKPalette() {
  const open = useEditor((s) => s.cmdKOpen);
  const close = useEditor((s) => s.closeCmdK);
  const setToast = useEditor((s) => s.setToast);
  const openGenerate = useEditor((s) => s.openGenerate);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const aiSuggestions: Item[] = [
    {
      icon: 'sun',
      text: 'Gor scen 2 ljusare (+15%)',
      ai: true,
      onClick: () => {
        close();
        setToast('Ljussattning applicerad pa scen 2');
      },
    },
    {
      icon: 'wand-2',
      text: 'Stabilisera skakig kamera i scen 4',
      ai: true,
      onClick: () => {
        close();
        setToast('Stabilisering pagar...');
      },
    },
    {
      icon: 'music',
      text: 'Lagg till filmisk bakgrundsmusik',
      ai: true,
      onClick: () => {
        close();
        setToast('Forslag pa musik visas...');
      },
    },
    {
      icon: 'captions',
      text: 'Generera captions fran tal (svenska)',
      ai: true,
      onClick: () => {
        close();
        setToast('Captions genereras...');
      },
    },
  ];

  const commands: Item[] = [
    { icon: 'scissors', text: 'Dela scen vid spelhuvud', shortcut: 'Ctrl+S' },
    { icon: 'copy', text: 'Duplicera aktuell scen', shortcut: 'Ctrl+D' },
    { icon: 'upload', text: 'Importera media...', shortcut: 'Ctrl+O' },
    {
      icon: 'sparkles',
      text: 'Oppna AI-skapande',
      shortcut: 'G',
      ai: true,
      onClick: () => {
        close();
        openGenerate();
      },
    },
    { icon: 'download', text: 'Exportera till...', shortcut: 'Ctrl+E' },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-[8px] flex items-start justify-center pt-[110px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="w-[90%] max-w-[640px] bg-bg-elevated border border-border-strong rounded-xl overflow-hidden shadow-2xl animate-[modalIn_0.2s_ease]">
        <div className="px-5 py-4 border-b border-border flex items-center gap-3">
          <Icon name="sparkles" size={18} className="text-ai" />
          <input
            ref={inputRef}
            placeholder="Skriv kommando eller fraga AI nagot..."
            autoComplete="off"
            className="flex-1 bg-transparent border-0 text-text text-base font-sans outline-none placeholder:text-text-subtle"
          />
          <button title="Rostinmatning">
            <Icon name="mic" size={16} className="text-text-muted cursor-pointer" />
          </button>
        </div>
        <div className="max-h-[440px] overflow-y-auto p-2">
          <SectionLabel>AI-forslag baserat pa aktuell scen</SectionLabel>
          {aiSuggestions.map((item, i) => (
            <ItemRow key={`ai-${i}`} item={item} />
          ))}
          <div className="h-px bg-border my-1.5" />
          <SectionLabel>Kommandon</SectionLabel>
          {commands.map((item, i) => (
            <ItemRow key={`cmd-${i}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-text-subtle px-3 pt-2 pb-1.5 font-semibold">
      {children}
    </div>
  );
}

function ItemRow({ item }: { item: Item }) {
  return (
    <button
      type="button"
      onClick={item.onClick}
      className="w-full px-3 py-2.5 rounded-md flex items-center gap-3 cursor-pointer text-text text-sm hover:bg-bg-panel-hover text-left"
    >
      <span className={`w-5 flex justify-center flex-shrink-0 ${item.ai ? 'text-ai' : 'text-text-muted'}`}>
        <Icon name={item.icon} size={16} />
      </span>
      <span>{item.text}</span>
      {item.shortcut && <Kbd className="ml-auto">{item.shortcut}</Kbd>}
    </button>
  );
}
